import { fromJS, List, Record } from 'immutable';
import {
  generateKeys,
  generateSchema,
  setInModel,
} from '../../../../utils/helpers/model.helper';
import { CustomField } from '../custom-field.model';
import { DocNumber } from './doc-number.model';
import { DocumentNotes } from './document-notes.model';
import { Labels } from './labels.model';
import { Rendering } from './rendering.model';

export const DOCUMENT_PRESET_SETTINGS_SCHEMA = Object.freeze({
  TERMS: 30,
  CURRENCY_CODE: '',
  LOCALE: '',
  SHOW_PRODUCT_CODE: false,
  SHOW_QUANTITY_AND_RATE: false,
  SHOW_SHIPPING: false,
  SHOW_STATIC_SIGNATURES_FIELD: false,
  SHOW_DUE_DATE: true,
  SHOW_PAYMENT_TERMS: true,
  SHOW_TAX_COLUMN: true,
  HIDE_COMPANY_NAME: false,
  SEPARATE_PARTS_AND_LABOR: false,
  REMINDERS_DISABLED: false,
  BANK_TRANSFER_DISABLED: false,
  CARD_PAYMENTS_DISABLED: false,
  PAYMENT_DETAILS: '',
  TERMS_AND_CONDITIONS: '',
  SIGNATURE_DECLARATION: '',
  PAYPAL_EC_DISABLED: false,
  SHOW_TAX_BREAKDOWN: false,
  CUSTOM_FIELDS: List<CustomField>([]),
  RENDERING: Rendering,
  NOTES: DocumentNotes,
  DOC_NUMBER: DocNumber,
});
const RECORD = generateSchema(DOCUMENT_PRESET_SETTINGS_SCHEMA);
const KEYS: any = generateKeys(DOCUMENT_PRESET_SETTINGS_SCHEMA);

export class DocumentPresetSettings extends Record(RECORD) {
  static KEYS: any = KEYS;

  constructor(props?: any) {
    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    super(props);

    const selfModel = setInModel(this, [
      {
        key: KEYS.CUSTOM_FIELDS,
        value: props.get(KEYS.CUSTOM_FIELDS)
          ? this.customFields.map((params) => new CustomField(params))
          : List<CustomField>([]),
      },
      {
        key: KEYS.RENDERING,
        value: new Rendering(this.rendering),
      },
      {
        key: KEYS.NOTES,
        value: new DocumentNotes(this.notes),
      },
      {
        key: KEYS.DOC_NUMBER,
        value: new DocNumber(this.docNumber),
      },
    ]);

    return selfModel as DocumentPresetSettings;
  }

  get terms(): number {
    return this.get(KEYS.TERMS);
  }

  get locale(): string {
    return this.get(KEYS.LOCALE);
  }

  get remindersDisabled(): boolean {
    return this.get(KEYS.REMINDERS_DISABLED);
  }

  get separatePartsAndLabor(): boolean {
    return this.get(KEYS.SEPARATE_PARTS_AND_LABOR);
  }

  get termsAndConditions(): string {
    return this.get(KEYS.TERMS_AND_CONDITIONS);
  }

  get showDueDate(): boolean {
    return this.get(KEYS.SHOW_DUE_DATE);
  }

  get showPaymentTerms(): boolean {
    return this.get(KEYS.SHOW_PAYMENT_TERMS);
  }

  get showShipping(): boolean {
    return this.get(KEYS.SHOW_SHIPPING);
  }

  get showSignature(): boolean {
    return this.get(KEYS.SHOW_STATIC_SIGNATURES_FIELD);
  }

  get showProductCode(): boolean {
    return this.get(KEYS.SHOW_PRODUCT_CODE);
  }

  get showQuantityAndRate(): boolean {
    return this.get(KEYS.SHOW_QUANTITY_AND_RATE);
  }

  get showTaxColumn(): boolean {
    return this.get(KEYS.SHOW_TAX_COLUMN);
  }

  get paymentDetails(): string {
    return this.get(KEYS.PAYMENT_DETAILS);
  }

  get currencyCode(): string {
    return this.get(KEYS.CURRENCY_CODE);
  }

  get customFields(): List<CustomField> {
    return this.get(KEYS.CUSTOM_FIELDS);
  }

  get docNumber(): DocNumber {
    return this.get(KEYS.DOC_NUMBER);
  }

  get rendering(): Rendering {
    return this.get(KEYS.RENDERING);
  }

  get documentLabels(): Labels {
    return this.rendering.labels;
  }

  get showTaxBreakdown(): boolean {
    return this.get(KEYS.SHOW_TAX_BREAKDOWN);
  }

  get hideCompanyName(): boolean {
    return this.get(KEYS.HIDE_COMPANY_NAME);
  }

  get notes(): DocumentNotes {
    return this.get(KEYS.NOTES);
  }

  get i2gMoneyDisabled(): boolean {
    return this.get(KEYS.I2G_MONEY_DISABLED);
  }

  get bankTransferDisabled(): boolean {
    return this.get(KEYS.BANK_TRANSFER_DISABLED);
  }

  get cardPaymentsDisabled(): boolean {
    return this.get(KEYS.CARD_PAYMENTS_DISABLED);
  }

  get paypalEcDisabled(): boolean {
    return this.get(KEYS.PAYPAL_EC_DISABLED);
  }
}
